/**
 * Reading a payment, without a component in the way. Pure and node-testable,
 * ported from `asthra-v4-web/src/billing/payments.ts`.
 */

import type { PaymentIntent } from "../../api/client";
import { formatMoney, formatUsd } from "./money";

/** What a payment bought, in words. Claim ref is the discriminator: `doc` vs `doc#9`. */
export function describePurchase(intent: PaymentIntent): string {
  const sections = sectionsIn(intent.doc_ids);
  if (sections.length === 0) return "The whole document";
  return sections.length === 1 ? "1 section" : `${String(sections.length)} sections`;
}

/** The section numbers a set of claim refs names, in the order given. */
export function sectionsIn(refs: readonly string[]): readonly string[] {
  return refs.flatMap((ref) => {
    const hash = ref.indexOf("#");
    return hash === -1 ? [] : [ref.slice(hash + 1)];
  });
}

/**
 * What the card was actually debited — `charged_amount_minor` in
 * `charged_currency`, falling back to the USD quote until a payment completes.
 */
export function paidAmount(intent: PaymentIntent): string {
  return intent.charged_amount_minor !== null && intent.charged_currency !== null
    ? formatMoney(intent.charged_amount_minor, intent.charged_currency)
    : formatUsd(intent.amount_minor);
}

/** Whether a "load older" control has anything behind it. Against the collection total. */
export function hasMore(loaded: number, total: number): boolean {
  return loaded < total;
}

/** Whether this payment is one somebody can still act on (an open, unpaid checkout). */
export function isOpen(intent: PaymentIntent): boolean {
  return intent.state === "created";
}

export interface ReceiptGroup {
  readonly label: string;
  readonly priceMinor: number;
  readonly count: number;
  /** The section numbers this group covers, empty for a whole document. */
  readonly sections: readonly string[];
}

/** The line items to show on a receipt, collapsed where they repeat (label + price). */
export function groupReceiptLines(intent: PaymentIntent): readonly ReceiptGroup[] {
  const groups = new Map<string, { label: string; priceMinor: number; sections: string[]; count: number }>();
  for (const line of intent.lines) {
    const key = `${line.label} ${String(line.price_minor)}`;
    const existing = groups.get(key);
    const section = line.ref.includes("#") ? line.ref.slice(line.ref.indexOf("#") + 1) : null;
    if (existing === undefined) {
      groups.set(key, {
        label: line.label,
        priceMinor: line.price_minor,
        sections: section === null ? [] : [section],
        count: 1,
      });
      continue;
    }
    existing.count += 1;
    if (section !== null) existing.sections.push(section);
  }
  return [...groups.values()].map((group) => ({
    label: group.label,
    priceMinor: group.priceMinor,
    count: group.count,
    sections: group.sections,
  }));
}

/** Our quote, summed from the frozen lines (recomputed, so it always adds up). */
export function subtotalMinor(intent: PaymentIntent): number {
  return intent.lines.reduce((sum, line) => sum + line.price_minor, 0);
}

/** The document this payment is about, however it was bought; `null` if it spans several. */
export function documentOf(intent: PaymentIntent): string | null {
  if (intent.run_doc_id !== null) return intent.run_doc_id;
  const docs = new Set(intent.doc_ids.map((ref) => ref.split("#", 1)[0] ?? ref));
  const [only] = [...docs];
  return docs.size === 1 && only !== undefined && only.length > 0 ? only : null;
}
